import axios from 'axios';

const DEFAULT_API_ERROR_MESSAGE =
  'Ha ocurrido un error de comunicacion con la API.';

export const DEFAULT_API_BASE_URL = 'https://api.suite.clasol.com/api/v1';
export const DEFAULT_API_TIMEOUT = 15000;
export const ADMIN_APPLICATION_CODE = 'SUITE_HOME';
export const LOGISTICS_APPLICATION_CODE = 'LOGISTICS';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.trim() || DEFAULT_API_BASE_URL;

export const API_TIMEOUT = Number.parseInt(
  process.env.REACT_APP_API_TIMEOUT || `${DEFAULT_API_TIMEOUT}`,
  10
);

let redirectingToLogin = false;

function isAuthenticationError(error) {
  return error?.response?.status === 401;
}

function isAuthEndpoint(requestUrl, endpoint) {
  return requestUrl === endpoint || requestUrl.startsWith(`${endpoint}?`);
}

function canRefreshRequest(originalRequest, requestUrl) {
  return (
    originalRequest &&
    !originalRequest._retry &&
    !isAuthEndpoint(requestUrl, '/auth/login') &&
    !isAuthEndpoint(requestUrl, '/auth/refresh')
  );
}

function shouldRedirectToLogin(requestUrl) {
  return (
    !isAuthEndpoint(requestUrl, '/auth/login') &&
    !isAuthEndpoint(requestUrl, '/auth/refresh')
  );
}

export function redirectToLogin() {
  if (
    typeof window === 'undefined' ||
    !window.location ||
    window.location.pathname === '/login' ||
    redirectingToLogin
  ) {
    return;
  }

  redirectingToLogin = true;
  window.location.assign('/login');
}

export function buildQueryParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) {
        return false;
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    })
  );
}

export function buildMultipartFormData(payload = {}) {
  if (payload instanceof FormData) {
    return payload;
  }

  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return;
    }

    formData.append(key, value);
  });

  return formData;
}

export function unwrapResponseData(response) {
  return response?.data;
}

export function extractApiErrorMessage(data) {
  if (!data) {
    return '';
  }

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors.find(
      (item) => typeof item === 'string' && item.trim()
    );

    if (firstError) {
      return firstError;
    }
  }

  return '';
}

export function getApiErrorMessage(
  error,
  fallbackMessage = DEFAULT_API_ERROR_MESSAGE
) {
  return (
    extractApiErrorMessage(error?.response?.data) ||
    error?.message ||
    fallbackMessage
  );
}

export function normalizeApiError(error) {
  if (!error) {
    return new Error(DEFAULT_API_ERROR_MESSAGE);
  }

  error.isApiError = true;
  error.status = error?.response?.status ?? null;
  error.data = error?.response?.data ?? null;
  error.message = getApiErrorMessage(error);

  return error;
}

export function buildApiHeaders({ applicationCode, headers = {} } = {}) {
  const resolvedHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  };

  if (applicationCode) {
    resolvedHeaders['x-application-code'] = applicationCode;
  }

  return resolvedHeaders;
}

export function createApiClient({
  applicationCode,
  baseURL = API_BASE_URL,
  timeout = API_TIMEOUT,
  headers,
  ...config
} = {}) {
  const instance = axios.create({
    baseURL,
    timeout,
    withCredentials: true,
    headers: buildApiHeaders({ applicationCode, headers }),
    ...config,
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const requestUrl = String(originalRequest?.url || '');

      if (isAuthenticationError(error) && canRefreshRequest(originalRequest, requestUrl)) {
        originalRequest._retry = true;

        try {
          await authApiClient.post('/auth/refresh');
          return instance(originalRequest);
        } catch {
          if (shouldRedirectToLogin(requestUrl)) {
            redirectToLogin();
          }

          return Promise.reject(normalizeApiError(error));
        }
      }

      if (isAuthenticationError(error) && shouldRedirectToLogin(requestUrl)) {
        redirectToLogin();
      }

      return Promise.reject(normalizeApiError(error));
    }
  );

  return instance;
}

export const publicApiClient = createApiClient();
export const authApiClient = createApiClient();
export const userApiClient = createApiClient();
export const adminApiClient = createApiClient({
  applicationCode: ADMIN_APPLICATION_CODE,
});
export const logisticsApiClient = createApiClient({
  applicationCode: LOGISTICS_APPLICATION_CODE,
});
export const integrationsApiClient = createApiClient({
  applicationCode: ADMIN_APPLICATION_CODE,
});

export const authApi = authApiClient;
export const userApi = userApiClient;
export const adminApi = adminApiClient;
export const logisticsHttp = logisticsApiClient;
export const integrationsHttp = integrationsApiClient;

function getPaginated(client, path, params = {}) {
  return client
    .get(path, {
      params: buildQueryParams(params),
    })
    .then(unwrapResponseData);
}

function postJson(client, path, payload = {}) {
  return client.post(path, payload).then(unwrapResponseData);
}

function patchJson(client, path, payload = {}) {
  return client.patch(path, payload).then(unwrapResponseData);
}

function putJson(client, path, payload = {}) {
  return client.put(path, payload).then(unwrapResponseData);
}

function deleteRequest(client, path) {
  return client.delete(path).then(unwrapResponseData);
}

export function buildLoginPayload(credentials = {}) {
  return {
    login: credentials?.login ?? credentials?.username ?? '',
    password: credentials?.password ?? '',
  };
}

export function login(credentials) {
  return authApi
    .post('/auth/login', buildLoginPayload(credentials))
    .then(unwrapResponseData);
}

export function getCurrentUser() {
  return authApi.get('/auth/me').then(unwrapResponseData);
}

export function logout() {
  return authApi.post('/auth/logout').then(unwrapResponseData);
}

export function getHealth() {
  return publicApiClient.get('/health').then(unwrapResponseData);
}

export function syncBusinessCentralPedidosExpediciones(payload = {}) {
  return postJson(
    integrationsApiClient,
    '/integrations/business-central/pedidos-expediciones',
    payload
  );
}

export function getLogisticsViewCustomers(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/view-customers', params);
}

export function getLogisticsViewDocuments(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/view-documents', params);
}

export function auditLogisticsExport(payload = {}) {
  return postJson(logisticsApiClient, '/logistics/exports/audit', payload);
}

export function exportLogisticsExcel(payload) {
  return logisticsApiClient.post('/logistics/exports/excel', payload, { responseType: 'blob', timeout: 60000 })
    .then(unwrapResponseData);
}

export function getLogisticsDocumentLine(id) {
  return logisticsApiClient
    .get(`/logistics/document-lines/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsDocumentLine(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/document-lines/${id}`, payload);
}

export function deleteLogisticsDocumentLine(id) {
  return deleteRequest(logisticsApiClient, `/logistics/document-lines/${id}`);
}

export function deleteLogisticsDocumentLines(ids) {
  return postJson(logisticsApiClient, '/logistics/document-lines/delete', { ids: ids.map(String) });
}

export function getLogisticsViewFields() {
  return logisticsApiClient
    .get('/logistics/view-fields')
    .then(unwrapResponseData);
}

export function getLogisticsViewProfiles(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/view-profiles', params);
}

export function createLogisticsViewProfile(payload) {
  return postJson(logisticsApiClient, '/logistics/view-profiles', payload);
}

export function updateLogisticsViewProfile(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/view-profiles/${id}`, payload);
}

export function setLogisticsViewProfileCustomers(id, payload) {
  return logisticsApiClient
    .put(`/logistics/view-profiles/${id}/customers`, payload)
    .then(unwrapResponseData);
}

export function createLogisticsCustomerViewProfile(payload) {
  return postJson(
    logisticsApiClient,
    '/logistics/customer-view-profiles',
    payload
  );
}

export function createLogisticsUserCustomerAccess(payload) {
  return postJson(logisticsApiClient, '/logistics/user-customer-access', payload);
}

export function getLogisticsPendingImports(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/pending-imports', params);
}

export function importLogisticsDocuments(payload = {}) {
  return postJson(logisticsApiClient, '/logistics/import-documents', payload);
}

export function syncLogisticsBusinessCentralMasterData(group) {
  return postJson(
    logisticsApiClient,
    `/logistics/business-central/master-data/${encodeURIComponent(group)}`
  );
}

export function getLogisticsClientes(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/clientes', params);
}

export function createLogisticsCliente(payload) {
  return postJson(logisticsApiClient, '/logistics/clientes', payload);
}

export function getLogisticsClienteById(id) {
  return logisticsApiClient
    .get(`/logistics/clientes/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsCliente(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/clientes/${id}`, payload);
}

export function deleteLogisticsCliente(id) {
  return deleteRequest(logisticsApiClient, `/logistics/clientes/${id}`);
}

export function getLogisticsTransportistas(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/transportistas', params);
}

export function createLogisticsTransportista(payload) {
  return postJson(logisticsApiClient, '/logistics/transportistas', payload);
}

export function getLogisticsTransportistaById(id) {
  return logisticsApiClient
    .get(`/logistics/transportistas/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsTransportista(id, payload) {
  return patchJson(
    logisticsApiClient,
    `/logistics/transportistas/${id}`,
    payload
  );
}

export function deleteLogisticsTransportista(id) {
  return deleteRequest(logisticsApiClient, `/logistics/transportistas/${id}`);
}

export function getLogisticsProveedores(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/proveedores', params);
}

export function createLogisticsProveedor(payload) {
  return postJson(logisticsApiClient, '/logistics/proveedores', payload);
}

export function getLogisticsProveedorById(id) {
  return logisticsApiClient
    .get(`/logistics/proveedores/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsProveedor(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/proveedores/${id}`, payload);
}

export function deleteLogisticsProveedor(id) {
  return deleteRequest(logisticsApiClient, `/logistics/proveedores/${id}`);
}

export function getLogisticsLogisticCenters(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/logistic-centers', params);
}

export function createLogisticsLogisticCenter(payload) {
  return postJson(logisticsApiClient, '/logistics/logistic-centers', payload);
}

export function getLogisticsLogisticCenterById(id) {
  return logisticsApiClient
    .get(`/logistics/logistic-centers/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsLogisticCenter(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/logistic-centers/${id}`, payload);
}

export function deleteLogisticsLogisticCenter(id) {
  return deleteRequest(logisticsApiClient, `/logistics/logistic-centers/${id}`);
}

export function getLogisticsProducts(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/products', params);
}

export function createLogisticsProduct(payload) {
  return postJson(logisticsApiClient, '/logistics/products', payload);
}

export function getLogisticsProductById(id) {
  return logisticsApiClient
    .get(`/logistics/products/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsProduct(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/products/${id}`, payload);
}

export function deleteLogisticsProduct(id) {
  return deleteRequest(logisticsApiClient, `/logistics/products/${id}`);
}

export function getLogisticsTransportTariffs(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/transport-tariffs', params);
}

export function createLogisticsTransportTariff(payload) {
  return postJson(logisticsApiClient, '/logistics/transport-tariffs', payload);
}

export function getLogisticsTransportTariffById(id) {
  return logisticsApiClient
    .get(`/logistics/transport-tariffs/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsTransportTariff(id, payload) {
  return patchJson(
    logisticsApiClient,
    `/logistics/transport-tariffs/${id}`,
    payload
  );
}

export function deleteLogisticsTransportTariff(id) {
  return deleteRequest(logisticsApiClient, `/logistics/transport-tariffs/${id}`);
}

export function previewLogisticsLoadOrderPrice(payload) {
  return postJson(
    logisticsApiClient,
    '/logistics/load-orders/price-preview',
    payload
  );
}

export function getLogisticsLoadOrders(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/load-orders', params);
}

export function getLogisticsTraceability(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/traceability', params);
}

export function getLogisticsLoadOrderStatuses(params = {}) {
  return logisticsApiClient
    .get('/logistics/load-order-statuses', { params })
    .then(unwrapResponseData);
}

export function createLogisticsLoadOrderStatus(payload) {
  return postJson(logisticsApiClient, '/logistics/load-order-statuses', payload);
}

export function updateLogisticsLoadOrderStatus(code, payload) {
  return patchJson(logisticsApiClient, `/logistics/load-order-statuses/${code}`, payload);
}

export function deleteLogisticsLoadOrderStatus(code) {
  return deleteRequest(logisticsApiClient, `/logistics/load-order-statuses/${code}`);
}

export function createLogisticsLoadOrder(payload) {
  return postJson(logisticsApiClient, '/logistics/load-orders', payload);
}

export function getLogisticsLoadOrderById(id) {
  return logisticsApiClient
    .get(`/logistics/load-orders/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsLoadOrder(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/load-orders/${id}`, payload);
}

export function cancelLogisticsLoadOrder(id) {
  return postJson(logisticsApiClient, `/logistics/load-orders/${id}/cancel`, {});
}

export function replaceLogisticsLoadOrderStops(id, stops = []) {
  return putJson(logisticsApiClient, `/logistics/load-orders/${id}/stops`, {
    stops,
  });
}

export function replaceLogisticsLoadOrderLines(id, lines = []) {
  return putJson(logisticsApiClient, `/logistics/load-orders/${id}/lines`, {
    lines,
  });
}

export function deleteLogisticsLoadOrder(id) {
  return deleteRequest(logisticsApiClient, `/logistics/load-orders/${id}`);
}

export function getLogisticsLoadOrderEmailSettings() {
  return logisticsApiClient
    .get('/logistics/load-order-email-settings')
    .then(unwrapResponseData);
}

export function createLogisticsLoadOrderEmailSetting(payload) {
  return postJson(
    logisticsApiClient,
    '/logistics/load-order-email-settings',
    payload
  );
}

export function updateLogisticsLoadOrderEmailSetting(id, payload) {
  return patchJson(
    logisticsApiClient,
    `/logistics/load-order-email-settings/${id}`,
    payload
  );
}

export function deleteLogisticsLoadOrderEmailSetting(id) {
  return deleteRequest(
    logisticsApiClient,
    `/logistics/load-order-email-settings/${id}`
  );
}

export function previewLogisticsLoadOrderEmails(id, payload = {}) {
  return postJson(
    logisticsApiClient,
    `/logistics/load-orders/${id}/emails/preview`,
    payload
  );
}

export function sendLogisticsLoadOrderEmails(id, payload = {}) {
  return postJson(
    logisticsApiClient,
    `/logistics/load-orders/${id}/emails/send`,
    payload
  );
}

export function sendLogisticsLoadOrderEmailsByScope(payload = {}) {
  return postJson(
    logisticsApiClient,
    '/logistics/load-orders/emails/send-by-scope',
    payload
  );
}

export function retryLogisticsEmailDelivery(id) {
  return postJson(logisticsApiClient, `/logistics/email-deliveries/${id}/retry`, {});
}

export function getNotificationEmailAccounts() {
  return adminApiClient.get('/notifications/email-accounts').then(unwrapResponseData);
}

export function getNotificationTemplates() {
  return adminApiClient.get('/notifications/templates').then(unwrapResponseData);
}

export function createNotificationTemplate(payload) {
  return postJson(adminApiClient, '/notifications/templates', payload);
}

export function getLogisticsReportTemplates(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/report-templates', params);
}

export function createLogisticsReportTemplate(payload) {
  return postJson(logisticsApiClient, '/logistics/report-templates', payload);
}

export function getLogisticsReportTemplateById(id) {
  return logisticsApiClient
    .get(`/logistics/report-templates/${id}`)
    .then(unwrapResponseData);
}

export function updateLogisticsReportTemplate(id, payload) {
  return patchJson(
    logisticsApiClient,
    `/logistics/report-templates/${id}`,
    payload
  );
}

export function deleteLogisticsReportTemplate(id) {
  return deleteRequest(logisticsApiClient, `/logistics/report-templates/${id}`);
}

export function generateLogisticsLoadOrderPdfReport(id, payload = {}) {
  return postJson(
    logisticsApiClient,
    `/logistics/load-orders/${id}/reports/pdf`,
    payload
  );
}

export function generateLogisticsLoadOrderExcelReport(id, payload = {}) {
  return postJson(
    logisticsApiClient,
    `/logistics/load-orders/${id}/reports/excel`,
    payload
  );
}

export function getLogisticsCmrFields() {
  return logisticsApiClient.get('/logistics/cmr-fields').then(unwrapResponseData);
}

export function getLogisticsCmrProfiles(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/cmr-profiles', params);
}

export function createLogisticsCmrProfile(payload) {
  return postJson(logisticsApiClient, '/logistics/cmr-profiles', payload);
}

export function updateLogisticsCmrProfile(id, payload) {
  return patchJson(logisticsApiClient, `/logistics/cmr-profiles/${id}`, payload);
}

export function deleteLogisticsCmrProfile(id) {
  return deleteRequest(logisticsApiClient, `/logistics/cmr-profiles/${id}`);
}

export function generateLogisticsLoadOrderCmrPdf(id, payload = {}) {
  return postJson(logisticsApiClient, `/logistics/load-orders/${id}/cmr/pdf`, payload);
}

export function getLogisticsLoadOrderEmailDeliveries(id) {
  return logisticsApiClient
    .get(`/logistics/load-orders/${id}/email-deliveries`)
    .then(unwrapResponseData);
}

export function getLogisticsEmailDeliveries(params = {}) {
  return getPaginated(logisticsApiClient, '/logistics/email-deliveries', params);
}

export function downloadFileBlob(uuid) {
  return logisticsApiClient
    .get(`/files/${uuid}/download`, { responseType: 'blob' })
    .then((response) => ({
      blob: response.data,
      fileName: fileNameFromContentDisposition(response.headers?.['content-disposition']),
      contentType: response.headers?.['content-type'],
    }));
}

function fileNameFromContentDisposition(value = '') {
  const match = String(value).match(/filename="([^"]+)"/i);
  return match?.[1] || '';
}

export function getLogisticsLoadOrderAttachments(id) {
  return logisticsApiClient
    .get(`/logistics/load-orders/${id}/attachments`)
    .then(unwrapResponseData);
}

export function createLogisticsLoadOrderAttachment(id, payload, config = {}) {
  const multipartPayload = buildMultipartFormData(payload);

  return logisticsApiClient
    .post(`/logistics/load-orders/${id}/attachments`, multipartPayload, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
    })
    .then(unwrapResponseData);
}

export const logisticsApi = {
  exportExcel: exportLogisticsExcel,
  getViewCustomers: getLogisticsViewCustomers,
  getViewDocuments: getLogisticsViewDocuments,
  getViewFields: getLogisticsViewFields,
  getViewProfiles: getLogisticsViewProfiles,
  createViewProfile: createLogisticsViewProfile,
  updateViewProfile: updateLogisticsViewProfile,
  createCustomerViewProfile: createLogisticsCustomerViewProfile,
  createUserCustomerAccess: createLogisticsUserCustomerAccess,
  getPendingImports: getLogisticsPendingImports,
  importDocuments: importLogisticsDocuments,
  syncBusinessCentralMasterData: syncLogisticsBusinessCentralMasterData,
  getClientes: getLogisticsClientes,
  createCliente: createLogisticsCliente,
  getClienteById: getLogisticsClienteById,
  updateCliente: updateLogisticsCliente,
  deleteCliente: deleteLogisticsCliente,
  getTransportistas: getLogisticsTransportistas,
  createTransportista: createLogisticsTransportista,
  getTransportistaById: getLogisticsTransportistaById,
  updateTransportista: updateLogisticsTransportista,
  deleteTransportista: deleteLogisticsTransportista,
  getProveedores: getLogisticsProveedores,
  createProveedor: createLogisticsProveedor,
  getProveedorById: getLogisticsProveedorById,
  updateProveedor: updateLogisticsProveedor,
  deleteProveedor: deleteLogisticsProveedor,
  getLogisticCenters: getLogisticsLogisticCenters,
  createLogisticCenter: createLogisticsLogisticCenter,
  getLogisticCenterById: getLogisticsLogisticCenterById,
  updateLogisticCenter: updateLogisticsLogisticCenter,
  deleteLogisticCenter: deleteLogisticsLogisticCenter,
  getProducts: getLogisticsProducts,
  createProduct: createLogisticsProduct,
  getProductById: getLogisticsProductById,
  updateProduct: updateLogisticsProduct,
  deleteProduct: deleteLogisticsProduct,
  getTransportTariffs: getLogisticsTransportTariffs,
  createTransportTariff: createLogisticsTransportTariff,
  getTransportTariffById: getLogisticsTransportTariffById,
  updateTransportTariff: updateLogisticsTransportTariff,
  deleteTransportTariff: deleteLogisticsTransportTariff,
  previewLoadOrderPrice: previewLogisticsLoadOrderPrice,
  getLoadOrders: getLogisticsLoadOrders,
  getLoadOrderStatuses: getLogisticsLoadOrderStatuses,
  createLoadOrderStatus: createLogisticsLoadOrderStatus,
  updateLoadOrderStatus: updateLogisticsLoadOrderStatus,
  deleteLoadOrderStatus: deleteLogisticsLoadOrderStatus,
  createLoadOrder: createLogisticsLoadOrder,
  getLoadOrderById: getLogisticsLoadOrderById,
  updateLoadOrder: updateLogisticsLoadOrder,
  cancelLoadOrder: cancelLogisticsLoadOrder,
  replaceLoadOrderStops: replaceLogisticsLoadOrderStops,
  replaceLoadOrderLines: replaceLogisticsLoadOrderLines,
  deleteLoadOrder: deleteLogisticsLoadOrder,
  getLoadOrderEmailSettings: getLogisticsLoadOrderEmailSettings,
  createLoadOrderEmailSetting: createLogisticsLoadOrderEmailSetting,
  updateLoadOrderEmailSetting: updateLogisticsLoadOrderEmailSetting,
  deleteLoadOrderEmailSetting: deleteLogisticsLoadOrderEmailSetting,
  deleteDocumentLine: deleteLogisticsDocumentLine,
  previewLoadOrderEmails: previewLogisticsLoadOrderEmails,
  sendLoadOrderEmails: sendLogisticsLoadOrderEmails,
  sendLoadOrderEmailsByScope: sendLogisticsLoadOrderEmailsByScope,
  retryEmailDelivery: retryLogisticsEmailDelivery,
  getNotificationEmailAccounts,
  getNotificationTemplates,
  createNotificationTemplate,
  getReportTemplates: getLogisticsReportTemplates,
  createReportTemplate: createLogisticsReportTemplate,
  getReportTemplateById: getLogisticsReportTemplateById,
  updateReportTemplate: updateLogisticsReportTemplate,
  deleteReportTemplate: deleteLogisticsReportTemplate,
  generateLoadOrderPdfReport: generateLogisticsLoadOrderPdfReport,
  generateLoadOrderExcelReport: generateLogisticsLoadOrderExcelReport,
  generateLoadOrderCmrPdf: generateLogisticsLoadOrderCmrPdf,
  downloadFileBlob,
  getLoadOrderAttachments: getLogisticsLoadOrderAttachments,
  createLoadOrderAttachment: createLogisticsLoadOrderAttachment,
};
